import * as moment from 'moment';

/**
 * "Weekly" everywhere in the app is the CALENDAR week that starts on the workspace's
 * first day of the week (Business profile > starting day; Monday when it is not set).
 * It is never "the last 7 days" (Rod, 1 Oct 2026). The quotes server applies the same
 * rule to its Weekly presets, reading the same setting.
 */
export interface CalendarWeek {
  start: moment.Moment;
  end: moment.Moment;
  prevStart: moment.Moment;
  prevEnd: moment.Moment;
}

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** 0 (Sunday) .. 6 from a day name ("Monday") or a number; Monday when unknown. */
export function weekStartDay(value: unknown): number {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 6) return value;
  const i = WEEKDAYS.indexOf(String(value ?? '').trim().toLowerCase());
  return i < 0 ? 1 : i;
}

/** The calendar week containing `now`, starting on `weekStart`, plus the week before it. */
export function calendarWeek(now: moment.Moment, weekStart: unknown): CalendarWeek {
  const first = weekStartDay(weekStart);
  const start = now.clone().subtract((now.day() - first + 7) % 7, 'days').startOf('day');
  return {
    start,
    end: start.clone().add(6, 'days').endOf('day'),
    prevStart: start.clone().subtract(7, 'days').startOf('day'),
    prevEnd: start.clone().subtract(1, 'day').endOf('day'),
  };
}

/*
 * The first day of the week is remembered per workspace whenever a page loads the
 * business profile, so a page that does not load it (the quote list) labels and asks
 * for the same week without an extra request. Keyed by workspace, so switching
 * workspace never borrows another one's setting. Storage failures are ignored: the
 * page then uses Monday.
 */
const storageKey = (merchantId: string) => `week_start_day:${merchantId}`;

function activeMerchantId(): string {
  try {
    return localStorage.getItem('merchant_id') || '';
  } catch {
    return '';
  }
}

/** Remembers `day` for `merchantId`, but only when that is the workspace in use. */
export function rememberWeekStart(merchantId: unknown, day: unknown): void {
  const id = merchantId == null ? '' : String(merchantId);
  if (!id || id !== activeMerchantId()) return;
  try {
    if (typeof day === 'string' && day.trim()) localStorage.setItem(storageKey(id), day.trim());
    else localStorage.removeItem(storageKey(id));
  } catch {
    // storage unavailable: pages fall back to Monday
  }
}

/** Remembers `day` for the workspace in use (the Business profile setting was changed). */
export function rememberActiveWeekStart(day: unknown): void {
  rememberWeekStart(activeMerchantId(), day);
}

/** The active workspace's remembered first day of the week ('' when unknown = Monday). */
export function rememberedWeekStart(): string {
  const id = activeMerchantId();
  if (!id) return '';
  try {
    return localStorage.getItem(storageKey(id)) || '';
  } catch {
    return '';
  }
}
